import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useQuickHomeworkEvaluation, { dateInIstanbul, firstQuickLesson, validQuickDate } from '../../shared/homework/useQuickHomeworkEvaluation';
import IncompleteQuickEvaluationDialog from '../../shared/homework/IncompleteQuickEvaluationDialog';
import TeacherHomeworkBoard from './TeacherHomeworkBoard';
import './teacherQuickHomework.css';

export default function TeacherQuickHomework({ onRequest, classCode, onBack, onCompleted }) {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [step, setStep] = useState(1);
  const [reload, setReload] = useState(0);
  const students = (options?.students || []).map(student => ({ ...student, id: String(student.student_id), isAbsent: student.is_absent }));
  const draft = useQuickHomeworkEvaluation({ students, onCompleted,
    send: payload => onRequest('quick-homework', { method: 'POST', body: JSON.stringify(payload) }),
  });
  const { setLesson, setFormError } = draft;
  useEffect(() => {
    let alive = true;
    setOptions(null); setLoadError('');
    onRequest('quick-homework/options').then(data => {
      if (!alive) return;
      if (!Array.isArray(data.lessons) || !Array.isArray(data.students)) throw new Error('Invalid options');
      setOptions(data); setLesson(current => current || String(firstQuickLesson(data.lessons)?.id || ''));
      if (!data.lessons.length) setFormError('Bu sınıf için uygun ödev dersi bulunamadı.');
    }).catch(error => { if (alive && error.status !== 401) setLoadError('Dersler ve öğrenciler yüklenemedi.'); });
    return () => { alive = false; };
  }, [onRequest, setLesson, setFormError, reload]);
  const lesson = options?.lessons.find(item => String(item.id) === draft.lesson);
  const submit = confirmed => draft.submit({ confirmed, valid: !!lesson,
    payload: { lesson_id: Number(draft.lesson), assigned_date: draft.assignedDate,
      records: draft.selectedStudents.map(student => ({ student_id: student.student_id, status: draft.evaluations[student.id] })) },
  });
  return <Box className="teacher-homework-board-shell teacher-quick-homework">
    <Button className="teacher-homework-board-back" disabled={draft.saving} onClick={step === 1 ? onBack : () => setStep(1)}>
      {step === 1 ? '← Ödev Listesine Dön' : '← Ders ve Tarihe Dön'}
    </Button>
    <ol className="tq-steps" aria-label="Hızlı değerlendirme adımları">
      <li aria-current={step === 1 ? 'step' : undefined}>1 · Ders ve Tarih</li>
      <li aria-current={step === 2 ? 'step' : undefined}>2 · Öğrenci Durumları</li>
    </ol>
    {loadError ? <Alert severity="error">{loadError} <Button onClick={() => setReload(value => value + 1)}>Yeniden Dene</Button></Alert>
      : !options ? <Box role="status"><CircularProgress size={28} /> Dersler ve öğrenciler yükleniyor…</Box> : <>
        {draft.formError && <Alert severity="error" sx={{ mb: 2 }}>{draft.formError}</Alert>}
        {step === 1 ? <Paper elevation={0} className="tq-setup">
          <Typography component="h1">⚡ Hızlı Ödev Değerlendir</Typography>
          <p className="th-subtitle">{options.class?.code || classCode} Sınıfı · Önceden kaydedilmemiş bir ödevi değerlendirin.</p>
          <Typography component="h2">Ders ve Tarih</Typography>
          <div className="tq-fields">
            <TextField select fullWidth label="Ders" value={draft.lesson} disabled={draft.locked || !options.lessons.length}
              onChange={event => setLesson(event.target.value)}>
              {options.lessons.map(item => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
            </TextField>
            <TextField fullWidth type="date" label="Ödevin Verildiği Tarih" value={draft.assignedDate} disabled={draft.locked}
              onChange={event => draft.setAssignedDate(event.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ max: dateInIstanbul() }} />
          </div>
          <Alert severity="info">Öğrenci durumları bu adımda kaydedilmez. Veli bildirimleri son kaydetmede işleme alınır.</Alert>
          <Button variant="contained" disabled={!lesson || !validQuickDate(draft.assignedDate) || !students.length || draft.saving}
            onClick={() => setStep(2)}>Öğrencileri Değerlendir →</Button>
          {!students.length && <Alert severity="info">Bu sınıfta değerlendirilecek öğrenci bulunamadı.</Alert>}
        </Paper> : <>
          <TeacherHomeworkBoard draft disabled={draft.locked} classCode={options.class?.code || classCode}
            homework={{ title: `${lesson?.name || 'Ödev'} — Hızlı Değerlendirme`, lessons: lesson ? [lesson] : [],
              assignedDate: draft.assignedDate, description: 'Durumları seçin. Henüz kayıt veya veli bildirimi oluşturulmadı.' }}
            students={students.map(student => ({ ...student, status: draft.evaluations[student.id] || null }))}
            onStatus={(student, status) => draft.setStatus(String(student.student_id), status)} onBulk={draft.markAllDone} />
          <Paper elevation={0} className="tq-submit">
            <div><strong>{draft.selectedStudents.length} / {students.length} öğrenci değerlendirildi</strong>
              <p>{draft.absentCount - draft.evaluatedAbsentCount} devamsız öğrenci değerlendirilmedi · Henüz bildirim gönderilmedi.</p></div>
            <Button variant="contained" color="success" disabled={draft.saving || !draft.selectedStudents.length}
              onClick={() => submit(false)}>{draft.saving ? 'Kaydediliyor…' : 'Kaydet ve Velilere Gönder'}</Button>
          </Paper>
        </>}
        <IncompleteQuickEvaluationDialog open={draft.confirmIncomplete} count={draft.remainingCount}
          onReturn={() => draft.setConfirmIncomplete(false)} onSubmit={() => submit(true)} />
      </>}
  </Box>;
}
